import { HttpError, NetworkError } from '@liminal/errors/http-errors';

/**
 * PUTs a blob straight to storage on a URL the server signed (LIM-4606),
 * reporting how much of it has gone.
 *
 * XMLHttpRequest rather than fetch because fetch reports no upload progress.
 * The headers are sent exactly as the server returned them: the signature
 * covers them (the Content-Type and the size range), so adding, dropping or
 * re-casing one makes storage refuse the PUT. The browser sets Content-Length
 * itself from the blob.
 *
 * Throws the same typed errors as adminClient: NetworkError when the PUT
 * never reached storage, HttpError when storage answered with a failure.
 */
export function putSignedUpload(params: {
  readonly uri: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: Blob;
  /** Called with the fraction sent, from 0 to 1. */
  readonly onProgress: (fraction: number) => void;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', params.uri);
    for (const [name, value] of Object.entries(params.headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) params.onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      const message = `Storage refused the upload${xhr.statusText ? `: ${xhr.statusText}` : ''}`;
      reject(
        new HttpError(message, xhr.status, undefined, {
          error: { message },
          status: xhr.status,
          statusText: xhr.statusText,
        }),
      );
    };
    xhr.onerror = () => reject(new NetworkError('The upload did not reach storage'));
    xhr.send(params.body);
  });
}
